// Recuperação automática de "stale deploy".
//
// O SPA faz code-splitting por rota (App.tsx: `lazy(() => import(...))`), então
// cada tela pesada vira um chunk hasheado: `EventLiveAdminPage-<hash>.js`. Cada
// deploy reconstrói o bundle (hashes novos) e o servidor APAGA a dist anterior
// (deploy.yml: `rm -rf /opt/faromaps/*`). Um navegador que ainda roda o
// index.html de um build antigo — aba deixada aberta, bf-cache, voltar/avançar —
// pede o chunk velho ao navegar pra uma rota lazy. Esse arquivo já não existe →
// `import()` toma 404 → "Failed to fetch dynamically imported module" → cai no
// ErrorBoundary ("Algo deu errado"). Mordeu em prod (events.faromaps.app/.../live).
//
// Fix correto: recarregar a página UMA vez. O index.html é servido `no-cache`
// (infra/nginx/admin.conf), então o reload puxa o index novo, que referencia os
// hashes novos, e a rota abre. Guard anti-loop: se o chunk continuar falhando
// mesmo após o reload (deploy genuinamente quebrado, não apenas stale), não
// reentra — deixa o ErrorBoundary mostrar o erro real em vez de recarregar pra
// sempre.

import { breadcrumb } from './sentry';

const RELOAD_GUARD_KEY = 'faromaps:chunk-reload-at';
// Janela em que um segundo reload é suprimido. Stale-deploy resolve no primeiro
// reload; se falhar de novo dentro disso, é deploy quebrado — não insistir.
const RELOAD_GUARD_WINDOW_MS = 15_000;

// Mensagens que os navegadores usam quando um módulo importado dinamicamente
// não carrega. Variam por engine — cobrir Chromium, Firefox e Safari.
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'module script load failed', // Safari
];

/// True quando o erro é um chunk lazy que não carregou (stale deploy ou rede),
/// e não um bug de runtime da própria tela.
export function isChunkLoadError(err: unknown): boolean {
  const msg =
    typeof err === 'string'
      ? err
      : err instanceof Error
        ? err.message
        : '';
  const lower = msg.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => lower.includes(p));
}

/// Recarrega no máximo 1x por janela. Retorna true se disparou o reload (o
/// caller deve parar de processar — a página vai trocar), false se o guard
/// impediu (loop provável: deixar o erro aparecer).
export function reloadForStaleChunk(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
  } catch {
    // sessionStorage indisponível (modo privado estrito de alguns navegadores).
    // Seguimos sem guard: recuperar é melhor que travar na tela de erro.
  }

  const now = Date.now();
  if (last && now - last < RELOAD_GUARD_WINDOW_MS) {
    // Já recarregamos há pouco e o chunk falhou de novo → não é só stale.
    breadcrumb('chunk reload suprimido (guard): provável deploy quebrado', 'chunk-reload');
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    /* ignore — sem guard, ver acima */
  }

  breadcrumb('recarregando por chunk stale (deploy novo)', 'chunk-reload');
  window.location.reload();
  return true;
}

/// Wirado em main.tsx. Vite dispara `vite:preloadError` no `window` quando um
/// `import()` dinâmico falha em carregar — pegamos antes do React lançar, então
/// no caminho feliz (build de prod) o usuário nem chega a ver o ErrorBoundary.
/// O `preventDefault` impede o Vite de re-lançar (que poluiria console/Sentry).
/// Só ativo no bundle de prod — em dev o Vite não usa o helper de preload.
export function installChunkReload(): void {
  window.addEventListener('vite:preloadError', (event: Event) => {
    event.preventDefault();
    reloadForStaleChunk();
  });
}
